import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IView, IViewContainer, OViewVisitOptions} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {SRC, srcRef} from "/@lib@/wsp/src.js";
import {DynGen} from "/@back@/wsp/views/dynGen.js";
import {BarActions} from "/@back@/commons/widgets/bars.js";
import {Action, IActionToggle} from "/@lib@/commons/actions.js";
import {CDM} from "/@lib@/commons/utils/cdm.js";
import {IO} from "/@lib@/commons/io/io.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {ITEM, JGenSkin} from "/@lib@/wsp/item.js";
import {ButtonActions} from "/@back@/commons/widgets/buttons.js";
import {Desk} from "/@lib@/commons/desk.js";

/**
 * View d'un item via un dynGen.
 */

export interface OItemDynGenInit extends OSkinableInit {
	reg?: IReg<IItemUiEnv>
	generators: IDatasItemDynGen[]
	srcRef?: srcRef,
	toolbar?: OItemDynGenToolbar | false,
	autoResize?: boolean
	/** Comportement sur le onView, si la frame est déjà loadée :
	 * 	home : retour à la home (comportement par défaut)
	 * 	refresh : refresh de la page courante
	 * 	nothing
	 */
	onViewBehaviour?: 'home' | 'refresh' | 'nothing'
}

export interface OItemDynGenToolbar {
	/** Code de la liste d'extpoints d'actions à ajouter à la toolbar**/
	actionsList?: string
	/** true par défaut si pertinent **/
	skinSelector?: boolean
	/** false par défaut **/
	refresh?: boolean
	/** true par défaut **/
	gotoHome?: boolean
	/** true par défaut **/
	openInNewTab?: boolean
	/** true par défaut **/
	copyUrl?: boolean
}

export interface ItemDynGen extends BaseElement {
	initialize(init: OItemDynGenInit): this;
}

export interface IDatasItemDynGen {
	/** Code du générateur */
	code: string,
	/** Libélé court du générateur affiché dans la liste */
	label: string,
	/** Icone custom liée au gen (ie indépendant du skin). Par défaut : icone web. */
	icon?: string //path de type :wspRes:...
	iconUrl?: string //url résolue dynamiquement à partir de 'icon',
	/** genProps (skin, etc) */
	genProps?: Jsonisable
}

export class ItemDynGen extends BaseElement implements IViewContainer, IView {
	reg: IReg<IItemUiEnv>;
	availableSkins: Dict<JGenSkin>;
	protected _currentGen: IDatasItemDynGen;
	protected _currentSkin: string;

	toolbar: HTMLDivElement;
	actionsBar: BarActions<ItemDynGen>;
	genSelector: ButtonActions<ItemDynGen>;
	skinSelector: ButtonActions<ItemDynGen>;
	view: DynGen;

	onViewBehaviour: 'home' | 'refresh' | 'nothing' = 'home';

	get currentGen(): IDatasItemDynGen {
		return this._currentGen;
	}

	set currentGen(gen: IDatasItemDynGen) {
		this._currentGen = gen;
		this._updateSkins();
		this.reloadFrame();
	}

	get currentSkin() {
		return this._currentSkin;
	}

	set currentSkin(skinCode: string) {
		this._currentSkin = skinCode;
		this.refresh();
		this.reloadFrame();
	}

	reloadFrame() {
		const config = this.view.configs;
		config.c = this._currentGen?.code;
		config.gp = Object.assign({}, this._currentGen?.genProps, {skin: this._currentSkin});
		this.view.genFrame.src = this.view.getDynGenUrl(config)?.url || "about:blank";
	}

	refreshFrame() {
		const config = this.view.configs;
		config.c = this._currentGen?.code;
		config.gp = Object.assign({}, this._currentGen?.genProps, {skin: this._currentSkin});
		this.view.genFrame.contentWindow.location.reload();
	}


	_initialize(init: OItemDynGenInit) {
		this.reg = this.findReg(init);

		if (init.onViewBehaviour) this.onViewBehaviour = init.onViewBehaviour;

		if (!init.generators || !init.generators.length) {
			ERROR.log("No generator specified");
			return;
		}

		const firstGen = init.generators[0];
		this._currentGen = firstGen;
		this._currentSkin = (firstGen.genProps && firstGen.genProps.skin) || 'default';

		if (!init.srcRef && ITEM.isHistoryOrTrashUri(this.reg.env.longDesc.srcUri)) {
			this._attach(this.localName, init, ITEM.isTrashUri(this.reg.env.longDesc.srcUri) ?
				<c-msg label="Vue non disponible pour un item en corbeille"/> : <c-msg label="Vue non disponible pour une entrée d'historique"/>);
			return;
		}

		this.view = new DynGen().initialize({
			reg: this.reg,
			w: this.reg.env.wsp.code,
			c: this._currentGen?.code,
			r: init.srcRef || SRC.srcRef(this.reg.env.longDesc),
			gp: this._currentGen?.genProps,
			autoResize: init.autoResize
		});

		if (!("toolbar" in init) || init.toolbar) {
			const toolbarConf: OItemDynGenToolbar = init.toolbar && typeof init.toolbar === "object" ? init.toolbar : {};
			this.actionsBar = new BarActions<ItemDynGen>().initialize({
				actions: this.reg.mergeLists<Action>("actions:wsp-item-dyngen", toolbarConf.actionsList),
				actionContext: this
			});

			if (init.generators.length > 1) {
				const genActions = [];

				for (const genDatas of init.generators) {
					if (!genDatas.iconUrl) {
						if (genDatas.icon) genDatas.iconUrl = this.reg.env.itemType.datas.wspResUrl.resolve(genDatas.icon).url;
						else genDatas.iconUrl = '/@skin@/wsp/views/generators/gen_web.svg';
					}
					genActions.push(new SelectGenAction(genDatas));
				}
				this.genSelector = new ButtonActions<ItemDynGen>().initialize({
					actions: genActions,
					actionContext: this,
					title: "Sélection du rendu"
				});
			}

			if (toolbarConf.skinSelector !== false) {
				this.skinSelector = new ButtonActions<ItemDynGen>().initialize({
					actions: [],
					actionContext: this,
					hidden: true,
					icon: "/@skin@/wsp/views/itemDynGen/skinSelect.svg",
					title: "Sélection de l'habillage graphique"
				});
			}

			this.toolbar = <div id="toolBar">
				<div id="selectors">
					{this.genSelector}
					{this.skinSelector}
				</div>
				{this.actionsBar}
			</div> as HTMLDivElement;

			// Actions par défaut
			if (toolbarConf.refresh === true) this.actionsBar.actions.push(refreshAction);
			if (toolbarConf.gotoHome !== false) this.actionsBar.actions.push(gotoHomeAction);
			if (toolbarConf.openInNewTab !== false) this.actionsBar.actions.push(openInNewTabAction);
			if (toolbarConf.copyUrl !== false) this.actionsBar.actions.push(copyUrlAction);
		}
		this._attach(this.localName, init, this.toolbar, this.view);
		this._updateSkins();
	}

	protected async _updateSkins() {
		if (this.skinSelector) {
			const {itemDynGenUrl} = this.reg.env.universe.wspServer.config;
			const skins = await itemDynGenUrl.fetchJson<JGenSkin[]>(IO.qs("cdaction", "GetSkins", "wspCd", this.reg.env.wsp.code, "refUri", this.view.configs.r, "cdGen", this.currentGen.code));
			this.availableSkins = {};
			for (const skin of skins) this.availableSkins[skin.code] = skin;
			if (!(this._currentSkin in this.availableSkins) && skins.length > 0) this._currentSkin = skins[0].code;
			if (skins.length > 1) {
				this.skinSelector.actions = skins.map((skin) => new SelectSkinAction(skin.code, skin.title));
				DOM.setHidden(this.skinSelector, false);
			} else {
				DOM.setHidden(this.skinSelector, true);
			}
		}
		this.refresh();
	}

	protected _refresh() {
		if (this.actionsBar) this.actionsBar.refreshContent();
		if (this.genSelector) {
			const genDatas = this.currentGen;
			this.genSelector.icon = genDatas.iconUrl;
			this.genSelector.label = genDatas.label;
		}
		if (this.hasSkinSelector() && this.availableSkins) {
			this.skinSelector.label = this.availableSkins[this.currentSkin].title;
		}

		// Hide de la toolbar si rien à afficher
		if (this.toolbar) DOM.setHidden(this.toolbar, !this.hasGenSelector() && !this.hasSkinSelector() && (!this.actionsBar || this.actionsBar.actions?.length === 0));
	}

	protected hasGenSelector(): boolean {
		return this.genSelector && !this.genSelector.hidden
	}

	protected hasSkinSelector(): boolean {
		return this.skinSelector && !this.skinSelector.hidden
	}

	_forceRefreshOnViewShown: boolean;

	onViewShown() {
		if (this.view) {
			if (this.view.genFrame.src === "about:blank") {
				//rechargement forcé en cas de réaffichage car peut dépendre d'autres items (ressources images)
				// non actuellement notifiées pour le refresh du gen auto du dynGen.
				this.reloadFrame();
			} else if (this._forceRefreshOnViewShown) {
				this.refreshFrame();
				this._forceRefreshOnViewShown = undefined;
			}
			this.view.onViewShown();
		}
	}

	onViewHidden(closed?: boolean): void {
		if (this.view) {
			if (!closed) {
				if (this.onViewBehaviour == 'home')
					this.view.genFrame.src = "about:blank"; //on reset la frame pour forcer son refresh au réaffichage de la vue.
				else if (this.onViewBehaviour == 'refresh')
					this._forceRefreshOnViewShown = true;
			}
			this.view.onViewHidden(closed);
		}
	}

	visitViews(visitor: (view: IView) => any): any {
		if (this.view) return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.view ? visitor(this.view) : Promise.resolve(undefined);
	}
}

REG.reg.registerSkin("wsp-item-dyngen", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1;
		flex-direction: column;
	}

	#toolBar {
		display: flex;
		min-height: 0;
		min-width: 0;
		justify-content: space-between;
	  border-bottom: 1px solid var(--border-color);
	}

	#toolBar[hidden] {
		display: none;
	}

	#selectors {
		display: flex;
	}

	wsp-dyngen {
	  flex: 1;
	  margin: 1px;
	}
`);

customElements.define("wsp-item-dyngen", ItemDynGen);

class SelectGenAction extends Action<ItemDynGen> {
	constructor(protected genDatas: IDatasItemDynGen) {
		super();
	}

	getLabel(ctx: ItemDynGen): string {
		return this.genDatas.label || super.getLabel(ctx);
	}

	getIcon(ctx: ItemDynGen): string {
		return this.genDatas.icon || super.getIcon(ctx);
	}

	isToggle(ctx: ItemDynGen): this is IActionToggle<ItemDynGen> {return true}

	getDatas(api: 'toggle', ctx: ItemDynGen): boolean | null {return ctx.currentGen === this.genDatas }

	execute(ctx: ItemDynGen, ev?: Event): any | "noPreventDefault" {
		ctx.currentGen = this.genDatas
	}
}

class SelectSkinAction extends Action<ItemDynGen> {
	constructor(id: string, label: string) {
		super(id);
		this._label = label;
	}

	isToggle(ctx: ItemDynGen): this is IActionToggle<ItemDynGen> {return true}

	getDatas(api: 'toggle', ctx: ItemDynGen): boolean | null {return ctx.currentSkin == this._id }

	execute(ctx: ItemDynGen, ev?: Event): any | "noPreventDefault" {
		ctx.currentSkin = this._id
	}
}

class ItemDynGenUrlAction extends Action {
	isEnabled(ctx: ItemDynGen): boolean {
		try {
			// Une exception est lancé si bloqué par la sécurité
			ctx.view.genFrame.contentWindow.location.href;
		} catch (e) {
			return false;
		}
		return true;
	}

	_getCurrentAppUrl(ctx: ItemDynGen) {
		try {
			const dynGen = ctx.view;
			const baseUrl = dynGen.getDynGenUrl({
				w: dynGen.configs.w,
				c: dynGen.configs.c,
				r: dynGen.configs.r,
				gp: dynGen.configs.gp,
				pp: ""
			});
			const currentLoc = dynGen.genFrame.contentWindow.location;
			if (baseUrl && currentLoc.href.indexOf(baseUrl.url) == 0) {
				const baseUrlObj = new URL(baseUrl.url);
				const appUrl = IO.resolveUrl("#" + CDM.stringify({
					dynGen: {
						w: dynGen.configs.w,
						c: dynGen.configs.c,
						r: dynGen.configs.r,
						gp: dynGen.configs.gp,
						pp: currentLoc.pathname.substr(baseUrlObj.pathname.length),
						qs: currentLoc.search ? currentLoc.search.substr(1) : undefined
					}
				}), null);
				return appUrl;
			} else {
				return currentLoc.href;
			}
		} catch (e) {
			ERROR.log("Unable to retrieve the dynGen url", e);
			return null;
		}
	}
}

const refreshAction = new Action<ItemDynGen>('refresh')
	.setIcon("/@skin@/commons/icons/refresh.svg")
	.setLabel("Actualiser")
	.setExecute(function (this: Action, ctx: ItemDynGen) {
		ctx.refreshFrame();
	});

const gotoHomeAction = new Action<ItemDynGen>('gotoHome')
	.setIcon("/@skin@/wsp/views/itemDynGen/gotoHome.svg")
	.setLabel("Accueil")
	.setExecute(function (this: Action, ctx: ItemDynGen) {
		ctx.reloadFrame();
	});

const copyUrlAction = new ItemDynGenUrlAction('copyUrl')
	.setIcon("/@skin@/wsp/views/itemDynGen/copyUrl.svg")
	.setLabel("Copier l'adresse de la page en cours")
	.setExecute(async function (this: ItemDynGenUrlAction, ctx: ItemDynGen) {
		const appUrl = this._getCurrentAppUrl(ctx);
		if (appUrl) {
			await navigator.clipboard.writeText(appUrl);
			POPUP.showNotifInfo("L'adresse a été copiée dans le presse-papier.", ctx)
		} else {
			POPUP.showNotifError("Impossible de copier l'adresse dans le presse-papier. Vérifiez dans votre navigateur que vous avez autorisé ce site à accéder au presse-papier.", ctx)
		}
	});

const openInNewTabAction = new ItemDynGenUrlAction('openInNewTab')
	.setIcon("/@skin@/wsp/views/itemDynGen/openInNewTab.svg")
	.setLabel(Desk.electron ? "Ouvrir dans le navigateur" : "Ouvrir dans un nouvel onglet")
	.setExecute(async function (this: ItemDynGenUrlAction, ctx: ItemDynGen) {
		const appUrl = this._getCurrentAppUrl(ctx);
		if (appUrl) {
			let resp = await IO.openUrlExternal(appUrl);
			if (typeof resp === "object") {
				ERROR.log(resp.msg);
				POPUP.showNotifError("Impossible d'ouvrir l'adresse dans le navigateur.", ctx)
			}
		} else POPUP.showNotifError("Impossible d'ouvrir l'adresse dans un nouvel onglet.", ctx)
	});


